import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import MaterialIcon from "../../../shared/components/MaterialIcon";
import {
  DashboardHeroMetric,
  DashboardMiniStat,
  DashboardProgressRow,
  DashboardAlertBanner,
  DashboardTrendBars,
  DashboardQuickAction,
  DashboardResourceCard,
  DashboardPanel,
  DashboardPageHeader,
  buildSentimentTrendBars,
  buildCaseDistributionBars,
  formatMetricValue,
} from "../../../shared/components/dashboard/DashboardBento";
import { useTranslation } from "../../../i18n";
import { fetchDeletionCaseStats } from "../casesApi";
import { loadReviewLocations } from "../platformConnectionsStorage";
import { fetchLocationStats } from "../reviewsApi";
import useClientAccountStatus from "../hooks/useClientAccountStatus";
import { accountStatusLabel, isPaidAccount } from "../../../lib/accountStatus";
import { isOnboardingComplete } from "../onboardingStorage";
import { hydrateConnectionsFromServer } from "../platformConnectionsStorage";
import { useCurrentUser, getUserFirstName } from "../../../shared/components/UserProfileBadge";
import PlatformBenefitAlerts from "../../../shared/components/PlatformBenefitAlerts";

const BASE = "/dashboard/client";

function formatRating(value, locale) {
  const formatted = Number(value).toFixed(1);
  return locale === "de" ? formatted.replace(".", ",") : formatted;
}

const ClientHomePage = () => {
  const navigate = useNavigate();
  const { t, locale } = useTranslation();
  const currentUser = useCurrentUser();
  const firstName = getUserFirstName(currentUser);
  const [locations, setLocations] = useState(() => loadReviewLocations());
  const [connectionsChecked, setConnectionsChecked] = useState(false);
  const locationId = locations[0]?.locationId ?? null;
  const { accountStatus, loading: accountLoading } = useClientAccountStatus();
  const [stats, setStats] = useState(null);
  const [caseStats, setCaseStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Ask the server what the company has connected before deciding anything:
  // a team member (or this user on a new device) starts with an empty cache.
  useEffect(() => {
    let active = true;
    hydrateConnectionsFromServer()
      .then((list) => {
        if (!active) return;
        if (list !== null) setLocations(loadReviewLocations());
      })
      .finally(() => {
        if (active) setConnectionsChecked(true);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!connectionsChecked) return;
    // Sub-users join an account that is already set up — never send them
    // through the "connect your first profile" onboarding.
    if (currentUser?.is_sub_user) return;
    if (!isOnboardingComplete() && locations.length === 0) {
      navigate("/dashboard/client/onboarding", { replace: true });
    }
  }, [connectionsChecked, locations.length, currentUser, navigate]);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const cases = await fetchDeletionCaseStats();
      setCaseStats(cases);

      if (locationId) {
        const statsData = await fetchLocationStats(locationId);
        setStats(statsData);
      } else {
        setStats(null);
      }
    } catch {
      setCaseStats(null);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const activeCases = (caseStats?.pending ?? 0) + (caseStats?.inProgress ?? 0);
  const paid = isPaidAccount(accountStatus);

  const quickActions = useMemo(
    () => [
      {
        title: t("home.needsReply"),
        description: t("home.needsReplyDesc"),
        to: `${BASE}/reviews?view=needs-reply`,
        icon: "inbox",
        iconTone: "primary",
      },
      {
        title: t("home.criticalReviews"),
        description: t("home.criticalReviewsDesc"),
        to: `${BASE}/reviews?view=critical`,
        icon: "warning",
        iconTone: "error",
      },
      {
        title: t("home.deletionCases"),
        description: t("home.deletionCasesDesc"),
        to: `${BASE}/deletion-cases`,
        icon: "gavel",
        iconTone: "secondary",
      },
      {
        title: t("home.connectPlatform"),
        description: t("home.connectPlatformDesc"),
        to: `${BASE}/platforms`,
        icon: "hub",
        iconTone: "yellow",
      },
    ],
    [t]
  );

  const resourceLinks = useMemo(
    () => [
      { to: `${BASE}/documentation`, label: t("home.workflow1"), icon: "description" },
      { to: `${BASE}/documentation`, label: t("home.defamationGuide"), icon: "policy" },
      { to: `${BASE}/reviews`, label: t("dashboardBento.clientResource3"), icon: "reviews" },
    ],
    [t]
  );

  const reviewTrendBars = useMemo(
    () => buildSentimentTrendBars(stats?.sentiment, t),
    [stats?.sentiment, t]
  );

  const caseTrendBars = useMemo(
    () => buildCaseDistributionBars(caseStats, t),
    [caseStats, t]
  );

  const goToCases = (filter = {}) => {
    navigate(`${BASE}/deletion-cases`, { state: filter });
  };

  return (
    <div className="bg-background font-body-md text-on-surface antialiased">
      <div className="mx-auto min-h-screen max-w-container-max px-margin-mobile py-stack-lg md:px-margin-desktop">
        <DashboardPageHeader
          eyebrow={t("home.overview")}
          title={
            firstName
              ? t("home.greeting", { name: firstName })
              : t("home.dashboardTitle")
          }
          subtitle={t("dashboardBento.clientHeroSubtitle")}
          actions={
            <>
              {!accountLoading ? (
                <span className="inline-flex items-center gap-2 rounded-full border border-outline-variant/40 bg-surface-container-lowest px-4 py-2 font-label-sm font-medium text-on-surface-variant">
                  <MaterialIcon name="verified" className="text-[16px] text-secondary" />
                  {t("home.planLabel")}: {accountStatusLabel(accountStatus, t)}
                </span>
              ) : null}
              <Link
                to={`${BASE}/billing`}
                className="inline-flex items-center gap-2 rounded-xl border border-outline-variant/50 bg-surface-container-lowest px-4 py-2.5 font-label-sm font-medium text-primary transition hover:border-primary/20 hover:shadow-soft"
              >
                <MaterialIcon name="payments" className="text-[18px] text-secondary" />
                {t("home.billingLink")}
              </Link>
            </>
          }
        />

        <PlatformBenefitAlerts variant="dashboard" dismissible />

        {!locationId ? (
          <div className="tonal-card mb-stack-lg overflow-hidden rounded-2xl border border-yellow-500/30 bg-gradient-to-r from-yellow-500/10 via-yellow-500/5 to-transparent p-stack-lg">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-yellow-500/15 text-yellow-700">
                  <MaterialIcon name="hub" className="text-[24px]" />
                </div>
                <div>
                  <h2 className="font-headline-md text-primary">{t("home.connectProfile")}</h2>
                  <p className="mt-1 font-body-md text-on-surface-variant">
                    {t("home.connectProfileHint")}
                  </p>
                </div>
              </div>
              <Link
                to={`${BASE}/platforms`}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3 font-label-sm font-semibold text-on-primary transition hover:bg-primary/90"
              >
                {t("home.connectPlatform")}
                <MaterialIcon name="arrow_forward" className="text-[18px]" />
              </Link>
            </div>
          </div>
        ) : null}

        <div className="mb-stack-lg grid grid-cols-1 gap-stack-md md:grid-cols-3">
          <DashboardHeroMetric
            label={t("metrics.reviewsTotal")}
            value={
              stats ? formatMetricValue(stats.totalReviews, locale) : loading ? "—" : "0"
            }
            footnote={t("metrics.reviewsTotalHint")}
            icon="reviews"
            iconTone="primary"
            borderClass="border-primary"
            trendLabel={
              (stats?.newToday ?? 0) > 0
                ? t("dashboardBento.newTodayShort", { count: stats.newToday })
                : null
            }
            trendIcon="today"
            loading={loading && !stats}
            onClick={() => navigate(`${BASE}/reviews`)}
          />
          <DashboardHeroMetric
            label={t("home.activeCasesLabel")}
            value={formatMetricValue(activeCases, locale)}
            footnote={t("home.activeCasesHint")}
            icon="gavel"
            iconTone="secondary"
            borderClass="border-secondary"
            trendIcon="gavel"
            loading={loading}
            onClick={() => navigate(`${BASE}/deletion-cases`)}
          />
          <DashboardHeroMetric
            label={t("metrics.reviewsAverage")}
            value={stats ? formatRating(stats.averageRating, locale) : loading ? "—" : "—"}
            footnote={t("metrics.reviewsAverageHint")}
            icon="star"
            iconTone="yellow"
            borderClass="border-yellow-500"
            trendLabel={
              (stats?.negative ?? 0) > 0
                ? t("dashboardBento.criticalShort", { count: stats.negative })
                : null
            }
            trendIcon="warning"
            accent={(stats?.negative ?? 0) > 0}
            loading={loading && !stats}
            onClick={() => navigate(`${BASE}/reviews?view=critical`)}
          />
        </div>

        <div className="mb-stack-lg grid grid-cols-1 gap-stack-lg lg:grid-cols-12">
          <div className="lg:col-span-4">
            <DashboardPanel title={t("dashboardBento.accountOverview")} icon="monitoring">
              <div className="space-y-4">
                <DashboardProgressRow
                  label={t("dashboardBento.planStatus")}
                  value={accountStatusLabel(accountStatus, t)}
                  percent={paid ? 100 : 35}
                  barClassName={paid ? "bg-secondary" : "bg-outline"}
                  icon="verified"
                />
                <DashboardProgressRow
                  label={t("dashboardBento.platformsConnected")}
                  value={formatMetricValue(locations.length, locale)}
                  percent={locations.length > 0 ? 100 : 8}
                  barClassName="bg-primary"
                  icon="hub"
                />
              </div>
              {stats?.sentiment ? (
                <DashboardAlertBanner
                  icon="insights"
                  title={t("home.sentimentSnapshot")}
                  message={t("dashboardBento.sentimentSummary", {
                    positive: Math.round(stats.sentiment.positivePercent),
                    negative: Math.round(stats.sentiment.negativePercent),
                  })}
                />
              ) : (
                <DashboardAlertBanner
                  icon="link"
                  title={t("home.connectProfile")}
                  message={t("home.connectForSentiment")}
                  variant="warning"
                />
              )}
            </DashboardPanel>
          </div>

          <div className="lg:col-span-8">
            <DashboardPanel title={t("dashboardBento.reviewCaseAnalytics")} icon="analytics">
              <div className="mb-stack-lg grid grid-cols-2 gap-4 sm:grid-cols-3">
                <DashboardMiniStat
                  label={t("metrics.reviewsNewToday")}
                  value={stats?.newToday}
                  locale={locale}
                  loading={loading && !stats}
                  icon="today"
                  iconTone="yellow"
                  dot
                  onClick={() => navigate(`${BASE}/reviews`)}
                />
                <DashboardMiniStat
                  label={t("metrics.reviewsCritical")}
                  value={stats?.negative}
                  locale={locale}
                  loading={loading && !stats}
                  icon="warning"
                  errorTone
                  onClick={() => navigate(`${BASE}/reviews?view=critical`)}
                />
                <DashboardMiniStat
                  label={t("metrics.casesTotal")}
                  value={caseStats?.total}
                  locale={locale}
                  loading={loading}
                  icon="assignment"
                  onClick={() => goToCases()}
                />
                <DashboardMiniStat
                  label={t("metrics.casesPending")}
                  value={caseStats?.pending}
                  locale={locale}
                  loading={loading}
                  icon="schedule"
                  onClick={() => goToCases({ statusGroup: "pending" })}
                />
                <DashboardMiniStat
                  label={t("metrics.casesInProgress")}
                  value={caseStats?.inProgress}
                  locale={locale}
                  loading={loading}
                  icon="sync"
                  iconTone="secondary"
                  onClick={() => goToCases({ statusGroup: "in_progress" })}
                />
                <DashboardMiniStat
                  label={t("metrics.casesCompleted")}
                  value={caseStats?.completed}
                  locale={locale}
                  loading={loading}
                  icon="check_circle"
                  highlight
                  onClick={() => goToCases({ statusGroup: "completed" })}
                />
              </div>
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <DashboardTrendBars
                  title={t("dashboardBento.sentimentTrend")}
                  bars={reviewTrendBars}
                  loading={loading && locationId && !stats}
                  locale={locale}
                  emptyMessage={
                    locationId
                      ? t("dashboardBento.chartEmpty")
                      : t("home.connectForSentiment")
                  }
                />
                <DashboardTrendBars
                  title={t("dashboardBento.caseDistribution")}
                  bars={caseTrendBars}
                  loading={loading && !caseStats}
                  locale={locale}
                  emptyMessage={t("dashboardBento.chartEmpty")}
                />
              </div>
            </DashboardPanel>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-stack-lg lg:grid-cols-12">
          <div className="lg:col-span-7">
            <h3 className="mb-stack-md inline-flex items-center gap-2 font-headline-md text-headline-md text-primary">
              <MaterialIcon name="bolt" className="text-[20px] text-secondary" />
              {t("home.quickActions")}
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {quickActions.map((action) => (
                <DashboardQuickAction key={action.to} {...action} />
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <h3 className="mb-stack-md inline-flex items-center gap-2 font-headline-md text-headline-md text-primary">
              <MaterialIcon name="menu_book" className="text-[20px] text-secondary" />
              {t("dashboardBento.resources")}
            </h3>
            <DashboardResourceCard
              title={t("home.defamationGuide")}
              description={t("home.defamationGuideDesc")}
              links={resourceLinks}
              icon="policy"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientHomePage;

